import React from 'react';
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { FaLocationArrow } from "react-icons/fa";
import { getWeatherConditionIcon } from '../utils';
import ScoreBadge from './ScoreBadge';
import BBQTip from './BBQTip';
import ForecastStrip from './ForecastStrip';

const CSS = `
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes grillFlicker {
    0%,94%,100% { text-shadow: 0 0 50px rgba(255,120,30,0.55), 0 0 100px rgba(255,80,0,0.25), 3px 3px 0 rgba(0,0,0,0.45); }
    95% { text-shadow: 0 0 15px rgba(255,120,30,0.25), 3px 3px 0 rgba(0,0,0,0.45); }
    96% { text-shadow: 0 0 50px rgba(255,120,30,0.55), 0 0 100px rgba(255,80,0,0.25), 3px 3px 0 rgba(0,0,0,0.45); }
  }
  .gong-divider { display: flex; align-items: center; gap: 10px; margin: 0 0 10px; }
  .gong-divider span { font-family: 'DM Mono', monospace; color: rgba(240,236,224,0.25); font-size: 10px; letter-spacing: 0.2em; white-space: nowrap; }
  .gong-divider::before, .gong-divider::after { content: ''; flex: 1; height: 1px; background: repeating-linear-gradient(90deg, rgba(240,236,224,0.2) 0, rgba(240,236,224,0.2) 3px, transparent 3px, transparent 7px); }
  .gong-verdict { text-align: center; margin-bottom: 8px; animation: fadeUp 0.45s ease both; }
  .gong-verdict-city { font-family: 'DM Mono', monospace; font-size: 11px; letter-spacing: 0.22em; color: rgba(240,236,224,0.3); text-transform: uppercase; margin-bottom: 2px; }
  .gong-verdict-word { font-family: 'Bebas Neue', sans-serif; font-size: clamp(52px, 8vw, 72px); line-height: 0.88; letter-spacing: 0.04em; display: inline-block; }
  .gong-verdict-word.yes { color: #ffaa44; text-shadow: 0 0 50px rgba(255,120,30,0.55), 0 0 100px rgba(255,80,0,0.25), 3px 3px 0 rgba(0,0,0,0.45); animation: grillFlicker 5s ease-in-out infinite; }
  .gong-verdict-word.no  { color: #7eb8d4; text-shadow: 0 0 35px rgba(100,160,200,0.3), 3px 3px 0 rgba(0,0,0,0.45); }
  .gong-verdict-msg { font-family: 'Josefin Sans', sans-serif; font-size: 13px; color: rgba(240,236,224,0.55); margin-top: 4px; letter-spacing: 0.04em; min-height: 18px; }
  .gong-info-row { display: flex; align-items: center; justify-content: center; gap: 24px; margin-bottom: 10px; flex-wrap: wrap; animation: fadeUp 0.45s 0.1s ease both; }
  .gong-stats { display: flex; flex-direction: column; gap: 5px; }
  .gong-stat { display: flex; align-items: center; gap: 10px; }
  .gong-stat-key { font-family: 'DM Mono', monospace; font-size: 10px; letter-spacing: 0.14em; color: rgba(240,236,224,0.3); text-transform: uppercase; width: 68px; text-align: right; }
  .gong-stat-val { font-family: 'Josefin Sans', sans-serif; font-size: 13px; font-weight: 600; letter-spacing: 0.04em; color: #f0ece0; }
  .gong-stat-val.big { font-size: 16px; }
  .gong-stat-val svg { vertical-align: middle; }
  .gong-toggle-wrap { display: flex; justify-content: center; margin-bottom: 10px; animation: fadeUp 0.45s 0.2s ease both; }
`;

const getBBQMessage = (score, localHour) => {
  if (localHour >= 23 || localHour < 8) return "It's pretty late... unless this is for a midnight snack? 🌙";
  if (score >= 80) return "What are you waiting for? Fire it up.";
  if (score >= 60) return "Solid conditions. Get the coals going.";
  if (score >= 40) return "It's a gamble — fortune favours the bold.";
  return "Leave the tongs in the drawer today.";
};

export default function WeatherDisplay({ weatherData, bbqScore, unit, onUnitChange, forecast }) {
  if (!weatherData) return null;

  const { temperature, description, condition, isDaytime, humidity, rainProbability, windDeg, windSpeed, timezone } = weatherData;
  const isGrillingTime = bbqScore >= 50;
  const localHour = new Date(Date.now() + timezone * 1000).getUTCHours();
  const bbqMessage = getBBQMessage(bbqScore, localHour);

  const displayTemp = unit === 'celsius' ? Math.round(temperature) : Math.round(temperature * 9/5 + 32);
  const displayWind = unit === 'celsius' ? Math.round(windSpeed * 3.6) : Math.round(windSpeed * 2.237);
  const tempUnit = unit === 'celsius' ? '°C' : '°F';
  const windUnit = unit === 'celsius' ? 'km/h' : 'mph';

  return (
    <>
      <style>{CSS}</style>

      <div className="gong-divider"><span>TODAY'S VERDICT</span></div>

      <div className="gong-verdict">
        <p className="gong-verdict-city">{weatherData.cityName}, {weatherData.country}</p>
        <div className={`gong-verdict-word ${isGrillingTime ? 'yes' : 'no'}`}>
          {isGrillingTime ? 'GRILL' : 'NO GRILL'}
        </div>
        <p className="gong-verdict-msg">{bbqMessage}</p>
      </div>

      <div className="gong-info-row">
        <ScoreBadge score={bbqScore} />
        <div className="gong-stats">
          <div className="gong-stat">
            <span className="gong-stat-key">Now</span>
            <span className="gong-stat-val big">{displayTemp}{tempUnit}</span>
          </div>
          <div className="gong-stat">
            <span className="gong-stat-key">Sky</span>
            <span className="gong-stat-val">{getWeatherConditionIcon(condition, isDaytime, description)} {description}</span>
          </div>
          <div className="gong-stat">
            <span className="gong-stat-key">Rain</span>
            <span className="gong-stat-val">{rainProbability}%</span>
          </div>
          <div className="gong-stat">
            <span className="gong-stat-key">Wind</span>
            <span className="gong-stat-val">
              <FaLocationArrow size={11} style={{ transform: `rotate(${windDeg}deg)`, marginRight: 4, opacity: 0.7 }} />
              {displayWind} {windUnit}
            </span>
          </div>
          <div className="gong-stat">
            <span className="gong-stat-key">Humidity</span>
            <span className="gong-stat-val">{humidity}%</span>
          </div>
        </div>
      </div>

      <div className="gong-toggle-wrap">
        <ToggleButtonGroup size="small" value={unit} exclusive onChange={onUnitChange}
          sx={{
            border: '1.5px solid rgba(240,236,224,0.14)', borderRadius: '2px', overflow: 'hidden',
            '& .MuiToggleButton-root': {
              fontFamily: "'DM Mono', monospace", fontSize: '11px', letterSpacing: '0.1em',
              color: 'rgba(240,236,224,0.4)', borderColor: 'transparent', padding: '5px 15px',
              '&.Mui-selected': { color: '#f0ece0', backgroundColor: 'rgba(240,236,224,0.1)' },
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
            },
          }}
        >
          <ToggleButton value="celsius">°C</ToggleButton>
          <ToggleButton value="fahrenheit">°F</ToggleButton>
        </ToggleButtonGroup>
      </div>

      <BBQTip weatherData={weatherData} />
      <ForecastStrip forecast={forecast} unit={unit} />
    </>
  );
}
